import { StringUtils } from '@localina/core';
import React from 'react';
import { formatMinutes, getTicks, ITimeRange, toPercent } from './RestaurantStatsTimeline';

interface IProps {
    range: ITimeRange;
}

// Width of the element, kept current as the layout changes; undefined until first measured.
const useElementWidth = <T extends HTMLElement>(): [React.RefObject<T>, number | undefined] => {
    const ref = React.useRef<T>(null);
    const [width, setWidth] = React.useState<number>();

    React.useLayoutEffect(() => {
        const element = ref.current;
        if (!element) {
            return;
        }
        setWidth(element.getBoundingClientRect().width);
        const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    return [ref, width];
};

export const RestaurantStatsTimeAxis: React.FC<IProps> = (props) => {
    const [ref, width] = useElementWidth<HTMLDivElement>();

    return (
        <div className="restaurant-stats__time-axis" aria-hidden={true} ref={ref}>
            {getTicks(props.range, width).map((tick) => (
                <span
                    key={tick.minutes}
                    className={StringUtils.combineStrings(['restaurant-stats__time-axis__tick', tick.align])}
                    style={{ left: `${toPercent(tick.minutes, props.range)}%` }}
                >
                    {formatMinutes(tick.minutes)}
                </span>
            ))}
        </div>
    );
};

// Same width as the axis below it, so it measures to the same ticks.
export const RestaurantStatsTimeGrid: React.FC<IProps> = (props) => {
    const [ref, width] = useElementWidth<HTMLDivElement>();

    return (
        <div className="restaurant-stats__time-grid" aria-hidden={true} ref={ref}>
            {getTicks(props.range, width)
                .filter((tick) => tick.align === 'center')
                .map((tick) => (
                    <span key={tick.minutes} style={{ left: `${toPercent(tick.minutes, props.range)}%` }} />
                ))}
        </div>
    );
};
